import {useEffect, useRef, useState} from 'react';
import {IUploadActionDetailDto} from '@vivli/features/dashboard/infrastructure/interface';
import {Size} from '@vivli/shared/theme';
import {IUser} from '@vivli/shared/infrastructure/interface';
import {StudyIpdDataPackageFeature} from '@vivli/shared/features/data-package/feature';
import {Subject} from 'rxjs';
import {debounceTime, first} from 'rxjs/operators';
import {useRefState} from '@vivli/shared/infrastructure/hook';
import {DefaultDataPackageListComponent, uploadContainerStyle} from '@vivli/features/studies/components';
import {
    AvailableIpdContentTypeEnum,
    DataPackageStatusEnum,
    DataPackageTypeEnum
} from '@vivli/features/studies/infrastructure/enum';
import {IDataRequest, IRequestedStudy} from '@vivli/features/data-requests/infrastructure/interface';
import {UploadActionCardView} from '@vivli/features/dashboard/views';
import {
    useDataPackageApiContext,
    useDataRequestsApiContext,
    useModalApiContext
} from '@vivli/core/infrastructure/context';

interface UploadActionCardWrapperComponentProps {
    dto: IUploadActionDetailDto;
    user?: IUser;
    onHeightChange?: (height: number) => void;
    handleRefreshAfterUpload?: () => void;
}

export const UploadActionCardWrapperComponent = ({
    dto,
    user,
    onHeightChange,
    handleRefreshAfterUpload
}: UploadActionCardWrapperComponentProps) => {
    const defaultRowHeight = 110;
    const [uploadInProgress, setUploadInProgress] = useState(false);
    const [finalizingInProgress, setFinalizingInProgress] = useState(false);
    const [showUploadRef, showUploadSection, setShowUploadSection] = useRefState(false);
    const currentRowHeightRef = useRef(defaultRowHeight);
    const [dataPackageReady, setDataPackageReady] = useState(false);
    const [dataPackageStatus, setDataPackageStatus] = useState<boolean>(false);
    const [templateDataUploaded, setTemplateDataUploaded] = useState(false);
    const [parentIsLoading, setParentIsLoading] = useState(false);

    const [activeDataRequest, setActiveDataRequest] = useState<IDataRequest>(null);
    const [activeRequestedStudy, setActiveRequestedStudy] = useState<IRequestedStudy>(null);
    const [dataPackageIdToUse, setDataPackageIdToUse] = useState<string>(dto.ipdDataPackageId);
    const uploadDivRef = useRef<HTMLDivElement>();
    const heightChangeSubRef = useRef<Subject<void>>(new Subject());
    const dataRequestsService = useDataRequestsApiContext();
    const dataPackageService = useDataPackageApiContext();
    const modal = useModalApiContext();
    const isFullPackage = dto.ipdContentType === AvailableIpdContentTypeEnum.Full;

    const handleDataPackageError = (error: string) => {
        setUploadInProgress(false);
        setFinalizingInProgress(false);
        setParentIsLoading(false);
        modal.error(
            `Problem uploading data package for study with Sponsor ID: ${dto.uniqueId} in Data Request ${dto.dataRequestId}. StudyIpdDataPackageId: ${dto.studyIpdDataPackageId}  ${error}`
        );
    };

    // temporary error handler for data package load errors - these are
    // caused by missing IPD data package Ids - createDataPackage has created the
    // missing id but currently it is not in sync with the DTO - until we fix that,
    // have the user refresh which will get the newly supplied Id
    const handleDataPackageLoadError = (error: string) => {
        setUploadInProgress(false);
        setFinalizingInProgress(false);
        setParentIsLoading(false);
        modal.error(`Please refresh the browser and then press Upload Data Package again.`);
    };

    const handleGetRequestError = (error: string) => {
        setUploadInProgress(false);
        setFinalizingInProgress(false);
        setParentIsLoading(false);
        modal.error(`Cannot get Data Request ${dto.dataRequestId} ${error}`);
    };

    const getRequestedStudy = (dataRequest: IDataRequest) => {
        return dataRequest?.requestedStudies.find((rs) => rs.sponsorProtocolId === dto.uniqueId);
    };

    const getRequest = (dto: IUploadActionDetailDto) => {
        dataRequestsService
            .getDataRequest(dto.dataRequestGuid)
            .pipe(first())
            .subscribe((dr) => {
                setActiveDataRequest(dr);
            }, handleGetRequestError);
    };

    const triggerHeightChanged = () => {
        heightChangeSubRef.current.next();
    };

    const updateCurrentRowHeight = () => {
        const currentHeight = uploadDivRef?.current?.getBoundingClientRect().height;
        const height = showUploadRef.current ? currentHeight + defaultRowHeight : defaultRowHeight;
        if (height !== currentRowHeightRef.current) {
            currentRowHeightRef.current = height;
            onHeightChange && onHeightChange(height);
        }
    };

    const loadDataPackage = (ipdDataPackageId: string, dataRequest: IDataRequest, requestedStudy: IRequestedStudy) => {
        let pkgId: string;
        if (!ipdDataPackageId || ipdDataPackageId === '') {
            pkgId = dto.thisRequestOnlyIpd || dto.isUnlisted ? requestedStudy?.ipdDataPackageId : requestedStudy?.studyIPDDataPackageId;
        } else {
            pkgId = ipdDataPackageId;
        }

        dataPackageService
            .getDataPackage(pkgId)
            .pipe(first())
            .subscribe((dataPackage) => {
                setShowUploadSection(true);
                setParentIsLoading(false);
                if (dataPackage.dataPackageType === DataPackageTypeEnum.StudyIPD) {
                    const templateUploaded = dataPackage.status === DataPackageStatusEnum.Submitted;
                    setTemplateDataUploaded(templateUploaded);
                }
            }, handleDataPackageLoadError);
    };

    const createDataPackage = () => {
        dataRequestsService
            .createDataPackageForStudy(dto.dataRequestGuid, dto.studyGuid)
            .pipe(first())
            .subscribe((dataRequest) => {
                const requestedStudy = getRequestedStudy(dataRequest);
                //reset is to take care of the case where initial value (dto.ipdDataPackageId) is null or empty
                if (dto.isUnlisted || dto.thisRequestOnlyIpd) {
                    setDataPackageIdToUse(requestedStudy?.ipdDataPackageId);
                    loadDataPackage(requestedStudy?.ipdDataPackageId, dataRequest, requestedStudy);
                } else {
                    setDataPackageIdToUse(requestedStudy?.studyIPDDataPackageId);
                    loadDataPackage(requestedStudy?.studyIPDDataPackageId, dataRequest, requestedStudy);
                }
            }, handleDataPackageError);
    };

    const handleOnPackageSubmitted = () => {
        dataRequestsService
            .getDataRequest(dto.dataRequestGuid)
            .pipe(first())
            .subscribe((dr) => {
                setShowUploadSection(false);
                setUploadInProgress(false);
                handleRefreshAfterUpload();
            }, handleDataPackageError);
    };

    const handleActionCardButtonClick = () => {
        if (dataPackageStatus) {
            return;
        }

        if (!activeDataRequest) {
            //sets the active data request
            const request = getRequest(dto);
        }
        if (uploadInProgress) {
            setUploadInProgress(false);
            setShowUploadSection(false);
            return;
        }

        setUploadInProgress(true);
        setParentIsLoading(true);
        createDataPackage();
    };

    useEffect(() => {
        const sub = heightChangeSubRef.current.pipe(debounceTime(100)).subscribe(() => {
            setTimeout(() => {
                updateCurrentRowHeight();
            }, 300);
        });

        return () => sub.unsubscribe();
    }, []);

    useEffect(() => {
        if (showUploadSection) {
            // when showing the uploader move the update row height call to the end of the stack
            // and give it a half second extra to ensure the component has fully rendered
            // so that the height is correct when the upload section is shown
            setTimeout(() => {
                updateCurrentRowHeight();
            }, 500);
        } else {
            // if hiding the uploader, close it immediately, no need to wait on rendering
            updateCurrentRowHeight();
        }
    }, [showUploadSection]);

    return (
        <div>
            <div>
                <UploadActionCardView
                    dto={dto}
                    onHeightChange={onHeightChange}
                    handleActionCardButtonClick={handleActionCardButtonClick}
                    parentIsLoading={parentIsLoading}
                />
            </div>
            <div ref={uploadDivRef} style={uploadContainerStyle(uploadInProgress)} onClick={(e) => e.stopPropagation()}>
                {dataPackageIdToUse && uploadInProgress && (
                    <div>
                        <div
                            style={{
                                paddingLeft: '10px',
                                paddingRight: Size.PADDING
                            }}
                        >
                            <DefaultDataPackageListComponent isFull={dto.ipdContentType === AvailableIpdContentTypeEnum.Full} />
                        </div>
                        <div style={{ overflowY: 'auto' }}>
                            <StudyIpdDataPackageFeature
                                dataPackageId={dataPackageIdToUse}
                                ipdContentType={dto.ipdContentType}
                                studyId={dto.studyGuid}
                                requestedStudyId={dto.studyGuid}
                                dataRequestId={dto.dataRequestGuid}
                                thisRequestOnlyIpd={dto.thisRequestOnlyIpd || dto.isUnlisted}
                                onFileAdding={() => triggerHeightChanged()}
                                onFileRemoved={() => triggerHeightChanged()}
                                onValidation={() => triggerHeightChanged()}
                                onPackageSubmitted={handleOnPackageSubmitted}
                                onReady={() => setDataPackageReady(true)}
                                showExternalLinks={false}
                                rowHeight={50}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
